import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import { LuLoaderCircle } from 'react-icons/lu';
import { AuthContext } from '../Context/authContext';
import AuthLayout from './atoms/AuthLayout';
import { StatusCard } from './atoms/AuthLayout/AuthLayout.styles';
import {
  CONNECT_LINKEDIN_ROUTE,
  LINKEDIN_STEP_ROUTE,
  isLinkedinStepPending,
  linkedinStepHinted,
} from '../helpers/linkedinStep';
import { ACCEPT_INVITE_ROUTE, getPendingInvite, pendingInviteUrl } from '../helpers/pendingInvite';
import { PAYWALL_ROUTE } from './atoms/SignupFlow/signupPlan';

/**
 * Where a signed-in user lands, and where every "you cannot be here" bounce
 * goes. AI Home is the default landing page.
 *
 * It is also the only safe fallback: it is always allowed (see isRouteAllowed),
 * whereas /dashboard can be absent from a workspace's allowedPages, in which
 * case redirecting there would bounce straight back here on the next render.
 */
const HOME_ROUTE = '/ai-home';

/**
 * Pages a signed-in account may open while it still owes signup step 3: the
 * step itself, the invite page (it has its own flow for signed-in accounts) and
 * the OAuth consent screen an AI client sends the user to mid-flow.
 */
const LINKEDIN_STEP_EXEMPT = [CONNECT_LINKEDIN_ROUTE, ACCEPT_INVITE_ROUTE, '/mcp-consent'];

/** Pages an account that has not started its trial may open: the paywall itself and the same two flows. */
const PAYWALL_EXEMPT = [PAYWALL_ROUTE, ACCEPT_INVITE_ROUTE, '/mcp-consent'];

const SigningIn = () => (
  <AuthLayout>
    <StatusCard>
      <span className="spin">
        <LuLoaderCircle />
      </span>
      <strong>Signing you in…</strong>
    </StatusCard>
  </AuthLayout>
);

const RouteGuard = ({ children, requireAuth = true }) => {
  const { isLoggedIn, user, authReady, loading_user, allowedPages } = useContextHook(AuthContext, v => ({
    isLoggedIn: v.isLoggedIn,
    user: v.user,
    authReady: v.authReady,
    loading_user: v.loading_user,
    allowedPages: v.allowedPages,
  }));

  const location = useLocation();

  if (requireAuth && !isLoggedIn) {
    return <Navigate to="/" replace />;
  }

  // The account last seen in this browser still owed step 3: hold the page back
  // until the account loads, rather than show it and then jump to the step.
  const userLoaded = Boolean((user?.data || user)?.id) && authReady && !loading_user;
  if (requireAuth && !userLoaded && linkedinStepHinted() && !LINKEDIN_STEP_EXEMPT.includes(location.pathname)) {
    return <SigningIn />;
  }

  // Only check email verification after user data is loaded
  if (isLoggedIn && user && authReady && !loading_user) {
    const userData = user.data || user;
    const { status, isEmailVerified } = userData;
    const currentPath = location.pathname;
    const stripeSubscriptionId = userData?.subscriptionDetail?.stripeSubscriptionId;
    const hasManualSubscription =
      typeof stripeSubscriptionId === 'string' && stripeSubscriptionId.startsWith('manual_subscription');

    // Only redirect to verify-otp if user data is loaded and email is actually not verified
    if (isEmailVerified === false && currentPath !== '/verify-otp') {
      return <Navigate to="/verify-otp" replace />;
    }

    if (isEmailVerified) {
      /**
       * A verified account still on `signup` has not started its trial: the
       * paywall comes first. Starting it runs /onboard-user, which grants the
       * trial credits and moves the account to `trial_active`. Invited members
       * never have `signup`, so they skip it.
       */
      const needsTrial = status === 'signup';
      if (needsTrial) {
        return PAYWALL_EXEMPT.includes(currentPath) ? children : <Navigate to={PAYWALL_ROUTE} replace />;
      }
      if (currentPath === PAYWALL_ROUTE) {
        return <Navigate to={HOME_ROUTE} replace />;
      }

      if (
        status === 'signup' ||
        status === 'trial_active' ||
        status === 'subscription_active' ||
        status === 'onboarding_completed'
      ) {
        // Manual subscription users should not access Teams route directly.
        if (hasManualSubscription && currentPath.startsWith('/teams')) {
          return <Navigate to={HOME_ROUTE} replace />;
        }

        // The onboarding pages used to be allowed to render here so workspace
        // setup could finish and hand off. They are skipped now, so they fall
        // through to the public-route redirect below and land on AI Home —
        // including for anyone who still has the URL open or bookmarked.

        // If user is on public routes (login, signup, etc.), redirect to dashboard
        const publicRoutes = [
          '/',
          '/sign-up',
          '/forgot-password',
          '/verify-otp',
          '/connect-linkedin-account',
          '/billing',
          '/verify-email',
          '/change-password',
          '/successful',
        ];

        if (publicRoutes.includes(currentPath)) {
          // Signed in to accept an invite: go back to it rather than home.
          const pendingInvite = getPendingInvite();
          if (pendingInvite) return <Navigate to={pendingInviteUrl(pendingInvite)} replace />;
          // const hasSeenTour = localStorage.getItem('hasSeenTour');
          // return <Navigate to={hasSeenTour ? '/dashboard' : '/get-started'} replace />;
          // A brand-new account finishes signup on step 3 before reaching AI Home.
          return <Navigate to={HOME_ROUTE} replace />;
        }

        // The LinkedIn step is part of signup: until it is connected or skipped, every
        // other page (typed URL, bookmark, back button) returns to AI Home, which shows it.
        if (
          isLinkedinStepPending(userData) &&
          currentPath !== LINKEDIN_STEP_ROUTE &&
          !LINKEDIN_STEP_EXEMPT.includes(currentPath)
        ) {
          return <Navigate to={LINKEDIN_STEP_ROUTE} replace />;
        }

        const isRouteAllowed = (currentPath, allowedPages) => {
          if (!allowedPages || allowedPages.length === 0) {
            return true;
          }

          // Always allow automation routes
          if (currentPath.includes('automation')) {
            return true;
          }

          // Always allow csv-upload route (guarded inside component)

          if (currentPath === '/csv-upload') {
            return true;
          }

          // Workforce and AI Home are client-only pages — they have no
          // permission row in workspace permissionDetails, so allowedPages
          // would never contain them.
          if (
            currentPath === '/workforce' ||
            currentPath === '/ai-home' ||
            // A signed-in account accepts an invite here (it must be signed in to).
            currentPath === ACCEPT_INVITE_ROUTE ||
            currentPath === CONNECT_LINKEDIN_ROUTE ||
            currentPath === '/ai-learning'
          ) {
            return true;
          }

          if (
            allowedPages.includes('/campaign-manager') &&
            (currentPath === '/campaign-manager' || currentPath.startsWith('/campaign-manager/'))
          ) {
            return true;
          }

          // First check for exact match
          if (allowedPages.includes(currentPath)) {
            return true;
          }

          // Check for dynamic route patterns (e.g., /lead-preview/:id)
          for (const allowedRoute of allowedPages) {
            // Convert route pattern to regex
            // Replace :param with regex pattern to match any value
            const pattern = allowedRoute
              .replace(/:[^/]+/g, '[^/]+') // Replace :id, :name, etc. with pattern
              .replace(/\//g, '\\/'); // Escape forward slashes

            const regex = new RegExp(`^${pattern}$`);

            if (regex.test(currentPath)) {
              return true;
            }
          }

          return false;
        };

        if (!isRouteAllowed(currentPath, allowedPages)) {
          return <Navigate to={HOME_ROUTE} replace />;
        }

        return children;
      }
    }
  }

  if (!requireAuth && !isLoggedIn) {
    return children;
  }

  if (!requireAuth && isLoggedIn) {
    // Signed in but the account is still loading: once it lands this page
    // redirects, so showing it now (the login form, say) only flashes it. The
    // invite page renders its own states for a signed-in account.
    if (!authReady || loading_user || !(user?.data || user)?.id) {
      if (location.pathname === ACCEPT_INVITE_ROUTE) return children;
      return <SigningIn />;
    }

    const userData = user.data || user;
    const { status, isEmailVerified } = userData;

    // Only redirect if user is verified and the account is live. `signup` counts
    // now that onboarding is skipped — it is what a just-verified account looks
    // like until /onboard-user lands, and without it that user sits on the login
    // page instead of going home.
    if (isEmailVerified && (status === 'signup' || status === 'trial_active' || status === 'subscription_active')) {
      return <Navigate to={HOME_ROUTE} replace />;
    }
  }

  return children;
};

export default RouteGuard;
